import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut, GoogleAuthProvider } from 'firebase/auth';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { firebaseAuth } from '@/lib/firebase';
import type { PartnerMeResponse } from '@/lib/api';

export type { PartnerMeResponse };

interface AuthState {
  firebaseUser:  FirebaseUser | null;
  partnerUser:   PartnerMeResponse | null;
  idToken:       string | null;
  loading:       boolean;
  error:         string | null;
}

interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<void>;
  signOut:          () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const API_BASE    = import.meta.env.VITE_API_URL    ?? 'http://localhost:3001';
const ADMIN_URL   = import.meta.env.VITE_ADMIN_URL  ?? 'http://localhost:3000';
const PORTAL_URL  = import.meta.env.VITE_PORTAL_URL ?? 'http://localhost:3002';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    partnerUser:  null,
    idToken:      null,
    loading:      true,
    error:        null,
  });
  const blockNextNullCallbackRef = useRef(false);

  async function fetchPartnerUser(token: string): Promise<{ type: 'partner' | 'tenant' | 'admin'; user: PartnerMeResponse | null }> {
    try {
      // Try /v1/partner/me FIRST — this is the authoritative check for the partner portal.
      // A user may exist in both the Tenant User and PartnerUser tables (e.g. invited
      // to a tenant AND a partner). The partner portal should always honour the partner
      // account when one exists, instead of short-circuiting on the tenant match.
      const partnerRes = await fetch(`${API_BASE}/v1/partner/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (partnerRes.ok) {
        const partnerUser = await partnerRes.json() as PartnerMeResponse;
        return { type: 'partner', user: partnerUser };
      }

      // No partner account — check /v1/me to determine the correct redirect target
      const res = await fetch(`${API_BASE}/v1/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { type: 'partner', user: null };
      const me = await res.json() as { userType?: string; isSuperAdminTenant?: boolean };

      if (me.isSuperAdminTenant) {
        return { type: 'admin', user: null };
      }
      if (me.userType === 'tenant') {
        return { type: 'tenant', user: null };
      }

      return { type: 'partner', user: null };
    } catch {
      return { type: 'partner', user: null };
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        if (blockNextNullCallbackRef.current) {
          blockNextNullCallbackRef.current = false;
          return;
        }
        setState({ firebaseUser: null, partnerUser: null, idToken: null, loading: false, error: null });
        return;
      }

      try {
        const token = await fbUser.getIdToken();
        const { type, user } = await fetchPartnerUser(token);

        if (type === 'admin') {
          await firebaseSignOut(firebaseAuth);
          setState({ firebaseUser: null, partnerUser: null, idToken: null, loading: false, error: 'superadmin' });
          setTimeout(() => { window.location.href = `${ADMIN_URL}?redirected_from=partner`; }, 2000);
          return;
        }

        if (type === 'tenant') {
          await firebaseSignOut(firebaseAuth);
          setState({ firebaseUser: null, partnerUser: null, idToken: null, loading: false, error: 'tenant' });
          setTimeout(() => { window.location.href = `${PORTAL_URL}?redirected_from=partner`; }, 2000);
          return;
        }

        // Block access — no platform account for this Google identity
        if (!user) {
          blockNextNullCallbackRef.current = true;
          await firebaseSignOut(firebaseAuth);
          setState({ firebaseUser: null, partnerUser: null, idToken: null, loading: false,
            error: 'No account found for this email. Contact your administrator.' });
          return;
        }

        setState({ firebaseUser: fbUser, partnerUser: user, idToken: token, loading: false, error: null });
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: String(err) }));
      }
    });
    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebaseAuth, provider);
    } catch {
      setState((s) => ({ ...s, loading: false, error: 'Sign-in failed. Please try again.' }));
    }
  }

  async function signOut() {
    await firebaseSignOut(firebaseAuth);
    setState({ firebaseUser: null, partnerUser: null, idToken: null, loading: false, error: null });
  }

  return (
    <AuthContext.Provider value={{ ...state, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}
